import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Calendar, Users, Play, Target, Zap, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import LeadFormModal from "@/components/LeadFormModal";

// Import testimonial images
import testimonialRaphael from "@/assets/testimonial-raphael-new.png";
import testimonialJacob from "@/assets/testimonial-jacob-new.png";
import testimonialAlex from "@/assets/testimonial-alex-new.png";
import articleEtudeCas from "@/assets/article-etude-cas-conseillere.jpg";

interface CaseStudy {
  id: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: { metric: string; value: string; description: string }[];
  testimonial?: { quote: string; author: string; role: string; image: string };
  image: string;
  featured?: boolean;
  articleSlug?: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: "conseillere-financiere",
    client: "Conseillère en sécurité financière",
    industry: "Services financiers",
    challenge: "Générer des leads qualifiés rapidement pour remplir son agenda de rendez-vous.",
    solution: "Stratégie hybride alliant contenu organique percutant et publicité ciblée sur Meta.",
    results: [
      { metric: "37", value: "rendez-vous", description: "générés en 10 jours" },
      { metric: "10", value: "jours", description: "pour remplir l'agenda" },
      { metric: "1500%", value: "ROI", description: "retour sur investissement" },
    ],
    image: articleEtudeCas,
    featured: true,
    articleSlug: "etude-de-cas-conseillere-securite-financiere-leads",
  },
  {
    id: "wisera",
    client: "Wisera Technologies",
    industry: "Tech / SaaS",
    challenge: "Augmenter la visibilité de la marque et générer des leads via les réseaux sociaux.",
    solution: "Création de contenu créatif sortant du cadre traditionnel, stratégie social media complète.",
    results: [
      { metric: "3x", value: "visibilité", description: "augmentation de la portée" },
      { metric: "Direct", value: "leads", description: "générés via contenu social" },
      { metric: "100%", value: "satisfaction", description: "collaboration continue" },
    ],
    testimonial: {
      quote: "Ce que j'aime avec l'Agence 8020, c'est qu'ils ne font pas juste exécuter : ils proposent des idées créatives qui sortent du cadre. Grâce à eux, Wisera a gagné en visibilité, et on reçoit maintenant des leads directement de notre contenu sur les réseaux sociaux.",
      author: "Raphael Gagné",
      role: "Wisera Technologies",
      image: testimonialRaphael,
    },
    image: testimonialRaphael,
  },
  {
    id: "courtier-immobilier",
    client: "Jacob Saey-Williamson",
    industry: "Immobilier",
    challenge: "Passer d'une présence en ligne incohérente à une stratégie qui génère des clients.",
    solution: "Refonte complète de la stratégie de contenu avec calendrier éditorial et production vidéo.",
    results: [
      { metric: "Direct", value: "contacts", description: "via les réseaux sociaux" },
      { metric: "Constante", value: "présence", description: "en ligne optimisée" },
      { metric: "100%", value: "satisfaction", description: "résultats mesurables" },
    ],
    testimonial: {
      quote: "L'Agence 8020 m'a vraiment aidé à booster ma présence en ligne. Avant, je postais au hasard… maintenant, les gens me contactent directement grâce à ce qu'ils voient sur mes réseaux sociaux.",
      author: "Jacob Saey-Williamson",
      role: "Courtier Immobilier",
      image: testimonialJacob,
    },
    image: testimonialJacob,
  },
  {
    id: "pilote-nascar",
    client: "Alex Guennette",
    industry: "Sport / Personal Branding",
    challenge: "Créer du contenu professionnel structuré pour développer sa marque personnelle.",
    solution: "Plans de tournage clairs et structurés, production professionnelle sans complexité.",
    results: [
      { metric: "Pro", value: "qualité", description: "contenu de niveau broadcast" },
      { metric: "Simple", value: "processus", description: "aucune prise de tête" },
      { metric: "100%", value: "satisfaction", description: "collaboration fluide" },
    ],
    testimonial: {
      quote: "Ce que j'aime avec l'Agence 8020, c'est qu'ils sont à l'écoute. On arrive avec nos idées, pis ils reviennent avec un plan de tournage clair, structuré, qui rend ça simple pour nous.",
      author: "Alex Guennette",
      role: "Pilote Nascar | Athlex",
      image: testimonialAlex,
    },
    image: testimonialAlex,
  },
];

const globalStats = [
  { icon: TrendingUp, value: "50M+", label: "Vues générées" },
  { icon: Users, value: "150+", label: "Clients satisfaits" },
  { icon: Play, value: "500+", label: "Vidéos produites" },
  { icon: Target, value: "1500%", label: "ROI moyen" },
];

const CaseStudies = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const featuredCase = caseStudies.find((c) => c.featured);
  const otherCases = caseStudies.filter((c) => !c.featured);

  return (
    <>
      <SEOHead
        title="Études de cas | Résultats clients"
        description="Découvrez comment nos clients génèrent des leads et des revenus grâce à nos stratégies de contenu. Études de cas avec résultats chiffrés."
        canonical="/etudes-de-cas"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Études de cas - Agence 8020",
          "description": "Études de cas clients avec résultats chiffrés",
          "url": "https://agence8020.com/etudes-de-cas",
          "mainEntity": {
            "@type": "ItemList",
            "itemListElement": caseStudies.map((cs, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": cs.client,
              "description": cs.challenge,
            })),
          },
        }}
      />

      <main id="main-content" className="min-h-screen bg-background">
        <Navbar />

        {/* Hero Section */}
        <section className="pt-32 pb-16 px-4 relative overflow-hidden">
          <div className="absolute inset-0 gradient-hero noise" aria-hidden="true" />
          <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[100px]" aria-hidden="true" />

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <span className="pill mb-6 inline-block">
                <Zap className="w-4 h-4 inline mr-2 text-primary" aria-hidden="true" />
                Résultats prouvés
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Nos clients <span className="text-gradient">génèrent</span> des leads prévisibles pour leur business
              </h1>
              <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-10">
                Découvrez comment entrepreneurs et PME du Québec génèrent des leads qualifiés grâce à nos stratégies de contenu.
              </p>
            </motion.div>

            {/* Global Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
            >
              {globalStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className="text-center p-6 rounded-2xl glass-glow"
                  >
                    <Icon className="w-8 h-8 text-primary mx-auto mb-3" aria-hidden="true" />
                    <div className="text-3xl md:text-4xl font-bold text-foreground mb-1">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Featured Case Study */}
        {featuredCase && (
          <section className="py-16 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative rounded-3xl overflow-hidden glass-glow"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent" aria-hidden="true" />
                <div className="relative grid md:grid-cols-2 gap-8 p-8 md:p-12">
                  <div className="flex flex-col justify-center">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium w-fit mb-4">
                      <Target className="w-4 h-4" aria-hidden="true" />
                      Étude de cas vedette
                    </span>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                      {featuredCase.client}
                    </h2>
                    <p className="text-muted-foreground mb-6">{featuredCase.challenge}</p>

                    <div className="grid grid-cols-3 gap-4 mb-8">
                      {featuredCase.results.map((result, index) => (
                        <div key={index} className="text-center p-4 rounded-xl bg-card/50">
                          <div className="text-2xl md:text-3xl font-bold text-primary">{result.metric}</div>
                          <div className="text-xs text-muted-foreground">{result.description}</div>
                        </div>
                      ))}
                    </div>

                    {featuredCase.articleSlug && (
                      <Link to={`/articles/${featuredCase.articleSlug}`}>
                        <Button variant="hero" size="lg" className="group w-fit">
                          Lire l'étude complète
                          <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </Button>
                      </Link>
                    )}
                  </div>

                  <div className="relative">
                    <img
                      src={featuredCase.image}
                      alt={`Étude de cas ${featuredCase.client}`}
                      className="w-full h-full object-cover rounded-2xl aspect-[4/3]"
                      loading="lazy"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Other Case Studies */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Plus de <span className="text-gradient">success stories</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Chaque client a une histoire unique. Découvrez comment nous avons aidé des entrepreneurs de divers secteurs.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherCases.map((caseStudy, index) => (
                <motion.div
                  key={caseStudy.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="group rounded-2xl overflow-hidden glass hover-lift"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={caseStudy.image}
                      alt={`Étude de cas ${caseStudy.client}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-card/80 backdrop-blur-sm text-xs font-medium">
                        {caseStudy.industry}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-heading text-xl font-bold mb-2">{caseStudy.client}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{caseStudy.challenge}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {caseStudy.results.slice(0, 2).map((result, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 text-primary text-xs font-medium"
                        >
                          <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                          {result.metric} {result.value}
                        </span>
                      ))}
                    </div>

                    {caseStudy.testimonial && (
                      <blockquote className="border-l-2 border-primary/30 pl-4 italic text-sm text-muted-foreground line-clamp-3">
                        "{caseStudy.testimonial.quote.slice(0, 100)}..."
                      </blockquote>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative rounded-3xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" aria-hidden="true" />
              <div className="absolute inset-0 noise" aria-hidden="true" />

              <div className="relative text-center py-16 px-8">
                <Calendar className="w-12 h-12 text-primary mx-auto mb-6" aria-hidden="true" />
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                  Prêt à écrire votre success story?
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                  Réservez un diagnostic gratuit pour découvrir comment générer des leads prévisibles pour votre business.
                </p>
                <Button
                  variant="hero"
                  size="xl"
                  onClick={() => setIsLeadModalOpen(true)}
                  className="group"
                >
                  Réserver mon diagnostic gratuit
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </main>

      <LeadFormModal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} />
    </>
  );
};

export default CaseStudies;
